// Knowledge-graph write helpers for the comedy app. Writes go directly to
// Supabase under the same RLS as reads (auth.uid() = user_id); per-user edge
// labels are auto-created on first use, mirroring the entitybox backend.
import { supabase } from '~/lib/supabase-browser';
import { instanceOfLabel, type Graph } from '~/lib/graph';
import type { KnowledgeNode, Edge, EdgeLabel } from '~/types/knowledge';

export const ENTITY_SCALAR_FIELDS = ['photo_url', 'style', 'biography', 'website', 'description'] as const;
export type EntityScalarField = (typeof ENTITY_SCALAR_FIELDS)[number];

export interface NewEntityInput {
  title: string;
  typeTitle: string; // existing type-node title, or a new one to create
  scalars: Partial<Record<EntityScalarField, string>>;
}

// Distinct titles of nodes used as `is instance of` targets — the type catalog.
// Deduped case-insensitively (first-seen casing wins) so 'bit' and 'Bit' don't
// show as two picker entries; createEntity matches types case-insensitively too.
export function allTypeTitles(g: Graph): string[] {
  const inst = instanceOfLabel(g);
  if (!inst) return [];
  const titlesByKey = new Map<string, string>();
  for (const e of g.edges) {
    if (e.edge_label_id !== inst.id || !e.to_id) continue;
    const title = g.nodeById.get(e.to_id)?.title;
    if (!title) continue;
    const key = title.trim().toLowerCase();
    if (!titlesByKey.has(key)) titlesByKey.set(key, title);
  }
  return [...titlesByKey.values()].sort((a, b) => a.localeCompare(b));
}

async function insertNode(userId: string, title: string): Promise<KnowledgeNode> {
  const { data, error } = await supabase
    .from('knowledge_nodes')
    .insert({ user_id: userId, title, is_self_node: false })
    .select('*')
    .single();
  if (error) throw error;
  return data as KnowledgeNode;
}

async function insertEdge(userId: string, fromId: string, toId: string | null, edgeLabelId: string, value?: unknown): Promise<Edge> {
  const { data, error } = await supabase
    .from('edges')
    .insert({ user_id: userId, from_id: fromId, to_id: toId, edge_label_id: edgeLabelId, ...(toId === null ? { value } : {}) })
    .select('*')
    .single();
  if (error) throw error;
  return data as Edge;
}

// Resolve a scalar edge label by name, preferring the user's own over a system
// one (matching how readers shadow them); auto-create per-user on miss.
async function resolveScalarLabel(g: Graph, userId: string, name: string): Promise<EdgeLabel> {
  const candidates = g.labels.filter((l) => l.label.trim().toLowerCase() === name.toLowerCase());
  const existing = candidates.find((l) => l.user_id === userId) ?? candidates.find((l) => l.user_id === null);
  if (existing) return existing;

  const { data, error } = await supabase
    .from('edge_labels')
    .insert({
      user_id: userId,
      label: name,
      description: null,
      source_constraint: null,
      target_type: 'scalar',
      scalar_type: 'string',
      target_constraint: null,
      constraints: {},
    })
    .select('*')
    .single();
  if (!error) return data as EdgeLabel;

  // 23505: someone created it concurrently (UNIQUE(user_id, label)) — re-select.
  if (error.code === '23505') {
    const { data: row, error: selectError } = await supabase
      .from('edge_labels')
      .select('*')
      .eq('label', name)
      .or(`user_id.is.null,user_id.eq.${userId}`)
      .limit(1)
      .single();
    if (selectError) throw selectError;
    return row as EdgeLabel;
  }
  throw error;
}

// Create an entity: the node itself, its `is instance of` edge (creating the
// type node when the type is new), and one scalar edge per non-empty field.
// The node insert is the point of no return — if later edges fail, the node
// persists and the thrown error names the fields that didn't save.
export async function createEntity(g: Graph, userId: string, input: NewEntityInput): Promise<KnowledgeNode> {
  const inst = instanceOfLabel(g);
  if (!inst) throw new Error('System "is instance of" edge label not found — cannot classify the new entity.');

  const typeTitle = input.typeTitle.trim();
  const existingType = g.nodes.find((n) => (n.title ?? '').trim().toLowerCase() === typeTitle.toLowerCase());
  const typeNode = existingType ?? (await insertNode(userId, typeTitle));

  const node = await insertNode(userId, input.title.trim());

  const failedFields: string[] = [];
  try {
    await insertEdge(userId, node.id, typeNode.id, inst.id);
  } catch {
    failedFields.push('type');
  }

  for (const field of ENTITY_SCALAR_FIELDS) {
    const value = input.scalars[field]?.trim();
    if (!value) continue;
    try {
      const label = await resolveScalarLabel(g, userId, field);
      await insertEdge(userId, node.id, null, label.id, value);
    } catch {
      failedFields.push(field);
    }
  }

  if (failedFields.length > 0) {
    throw new Error(`Entity "${node.title}" was created, but these fields failed to save: ${failedFields.join(', ')}.`);
  }
  return node;
}
